import useMileageRegister, {MileageRegisterCase} from '@di-shared/features/mileage/register/useMileageRegister';
import {subDate} from '@shared/dateUtils';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import React from 'react';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import Header from '@di-shared/features/common/Header';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {diApi} from '@di-shared/api/di';
import useApiError from '@di-shared/features/common/useApiError';
import {useNavigate} from 'react-router';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

interface MileageRegisterProps {
  vin: string;
}

function MileageRegister({vin}: MileageRegisterProps) {
  const {t} = useInferableTranslation();

  const navigate = useNavigate();
  const {openAlert} = useCcWebviewUtilsHelper();
  const {mileageRegisterCase, drvngDstncRgsde, drvngDstncEndde, seasonDrvngDstnc} = useMileageRegister(vin);
  const {resolveError} = useApiError();

  const [register] = usePendingHandler(
    async () => {
      WebviewUtils.loading(true);
      await diApi.ecoMileageController.updateVehicleLink({vin, requestBody: {vin}});
      await openAlert({
        title: t('T30306'),
        message: t('T30307'),
      });
      navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin));
      WebviewUtils.loading(false);
    },
    async (error) => {
      WebviewUtils.loading(false);
      await resolveError(error);
    }
  );

  switch (mileageRegisterCase) {
    case MileageRegisterCase.CASE1_1:
      return (
        <MileageRegisterCase1_1
          drvngDstncRgsde={drvngDstncRgsde}
          drvngDstncEndde={drvngDstncEndde}
          onRegister={register}
        />
      );
    case MileageRegisterCase.CASE1_2:
      return <MileageRegisterCase1_2 onRegister={register} />;
    case MileageRegisterCase.CASE2:
      return (
        <MileageRegisterCase2
          seasonDrvngDstnc={seasonDrvngDstnc}
          onRegister={register}
        />
      );
    case MileageRegisterCase.CASE3:
      return (
        <MileageRegisterCase3
          drvngDstncRgsde={drvngDstncRgsde}
          drvngDstncEndde={drvngDstncEndde}
          onRegister={register}
        />
      );
    case MileageRegisterCase.CASE4:
      return (
        <MileageRegisterCase4
          drvngDstncRgsde={drvngDstncRgsde}
          drvngDstncEndde={drvngDstncEndde}
          onRegister={register}
        />
      );
    default:
      throw new Error();
  }
}

export default MileageRegister;

export interface MileageRegisterCaseProps {
  drvngDstncRgsde: Date | null;
  drvngDstncEndde: Date | null;
  onRegister: () => void;
}

export interface MileageRegisterCase2Props {
  seasonDrvngDstnc: number;
  onRegister: () => void;
}

export function MileageRegisterCase1_1({drvngDstncRgsde, drvngDstncEndde, onRegister}: MileageRegisterCaseProps) {
  /**
   * https://www.figma.com/design/AhHLTF7ki28EaFSn24Mvr6/07.-%EC%84%9C%EC%9A%B8%EC%8B%9C-%EB%A7%88%EC%9D%BC%EB%A6%AC%EC%A7%80-%EC%97%B0%EA%B3%84?node-id=10482-40468&t=UbGDzewawrJpWmmZ-11
   * Case1-1
   * 정기마일리지 등록 필요 여부: Y
   * 계절관리제 등록 필요 여부: N
   * 서울시에 전송 필요한 데이터: 사진등록요청일자-1일자의 최종 누적주행거리 전송
   */
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={async () => {
              const isConfirm = await openConfirm({
                title: t('T30278'),
                message: t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''}),
                okText: t('T30279'),
                cancelText: t('T30280'),
              });
              // 예 아니오 버튼 반대
              if (isConfirm) {
                backOrClose();
              }
            }}
          />
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30281')}
            </h2>
            <p className="section_heading_desc">
              {t('T30185_$date', {
                date: drvngDstncRgsde ? CldrDatetimeFormatter.format(subDate(drvngDstncRgsde, {days: 1}), 'MMMd') : '',
              })}
            </p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="floating_guide_box">
          <div className="floating_guide_icon">
            <span className="ic ic_warning_24px"></span>
          </div>
          <span className="floating_guide_text">
            {t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''})}
          </span>
        </div>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}

export function MileageRegisterCase1_2({onRegister}: {onRegister: () => void}) {
  /**
   * https://www.figma.com/design/AhHLTF7ki28EaFSn24Mvr6/07.-%EC%84%9C%EC%9A%B8%EC%8B%9C-%EB%A7%88%EC%9D%BC%EB%A6%AC%EC%A7%80-%EC%97%B0%EA%B3%84?node-id=10482-40468&t=UbGDzewawrJpWmmZ-11
   * Case1-2
   * 정기마일리지 등록 필요 여부: N
   * 계절관리제 등록 필요 여부: Y
   * 서울시에 전송 필요한 데이터: 3/31의 최종 누적주행거리 전송
   */
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={async () => {
              const isConfirm = await openConfirm({
                title: t('T30278'),
                message: t('T30186_$date', {date: CldrDatetimeFormatter.format(new Date(0, 3, 30), 'MMMd')}),
                okText: t('T30279'),
                cancelText: t('T30280'),
              });
              // 예 아니오 버튼 반대
              if (isConfirm) {
                backOrClose();
              }
            }}
          />
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30281')}
            </h2>
            <p className="section_heading_desc">
              {t('T30185_$date', {
                date: CldrDatetimeFormatter.format(subDate(new Date(0, 2, 31), {days: 1}), 'MMMd'),
              })}
            </p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="floating_guide_box">
          <div className="floating_guide_icon">
            <span className="ic ic_warning_24px"></span>
          </div>
          <span className="floating_guide_text">{t('T30186_$date', {date: CldrDatetimeFormatter.format(new Date(0, 3, 30), 'MMMd')})}</span>
        </div>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}

export function MileageRegisterCase2({onRegister}: MileageRegisterCase2Props) {
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={async () => {
              const isConfirm = await openConfirm({
                title: t('T30278'),
                message: t('T30284_$date', {date: CldrDatetimeFormatter.format(new Date(0, 11, 16), 'MMMd')}),
                okText: t('T30279'),
                cancelText: t('T30280'),
              });
              // 예 아니오 버튼 반대
              if (isConfirm) {
                backOrClose();
              }
            }}
          />
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30282')}
            </h2>
            <p className="section_heading_desc">{t('T30283')}</p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="floating_guide_box">
          <div className="floating_guide_icon">
            <span className="ic ic_warning_24px"></span>
          </div>
          <span className="floating_guide_text">
            {t('T30284_$date', {date: CldrDatetimeFormatter.format(new Date(0, 11, 16), 'MMMd')})}
          </span>
        </div>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}

export function MileageRegisterCase3({drvngDstncRgsde, drvngDstncEndde, onRegister}: MileageRegisterCaseProps) {
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={async () => {
              const isConfirm = await openConfirm({
                title: t('T30278'),
                message: t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''}),
                //TODO: 이거 어느 날짜를 따라야 하는지 모르겠음
                okText: t('T30279'),
                cancelText: t('T30280'),
              });
              // 예 아니오 버튼 반대
              if (isConfirm) {
                backOrClose();
              }
            }}
          />
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30192')}
            </h2>
            <p className="section_heading_desc">{t('T30193')}</p>
          </div>
          <div className="mileage_submit_box">
            <strong className="mileage_submit_title">{t('T30188')}</strong>
            <p className="mileage_submit_desc">
              {t('T30189_$date', {date: CldrDatetimeFormatter.format(new Date(0, 2, 31), 'MMMd')})}
              <br />
              {t('T30190_$date', {date: CldrDatetimeFormatter.format(new Date(0, 3, 30), 'MMMd')})}
            </p>
          </div>
          <div className="mileage_submit_box">
            <strong className="mileage_submit_title">{t('T30191')}</strong>
            <p className="mileage_submit_desc">
              {t('T30189_$date', {
                date: drvngDstncRgsde ? CldrDatetimeFormatter.format(subDate(drvngDstncRgsde, {days: 1}), 'MMMd') : '',
              })}
              <br />
              {t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''})}
            </p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}

export function MileageRegisterCase4({drvngDstncRgsde, drvngDstncEndde, onRegister}: MileageRegisterCaseProps) {
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={async () => {
              const isConfirm = await openConfirm({
                title: t('T30278'),
                message: t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''}),
                okText: t('T30279'),
                cancelText: t('T30280'),
              });
              // 예 아니오 버튼 반대
              if (isConfirm) {
                backOrClose();
              }
            }}
          />
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30192')}
            </h2>
            <p
              className="section_heading_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30194')}
            </p>
          </div>
          <div className="mileage_submit_box">
            <strong className="mileage_submit_title">{t('T30188')}</strong>
            <p className="mileage_submit_desc">
              {t('T30189_$date', {date: CldrDatetimeFormatter.format(new Date(0, 10, 30), 'MMMd')})}
              <br />
              {t('T30190_$date', {date: CldrDatetimeFormatter.format(new Date(0, 11, 26), 'MMMd')})}
            </p>
          </div>
          <div className="mileage_submit_box">
            <strong className="mileage_submit_title">{t('T30191')}</strong>
            <p className="mileage_submit_desc">
              {t('T30189_$date', {
                date: drvngDstncRgsde ? CldrDatetimeFormatter.format(subDate(drvngDstncRgsde, {days: 1}), 'MMMd') : '',
              })}
              <br />
              {t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''})}
            </p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}

export function MileageRegisterLoading() {
  const {backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={() => {
              backOrClose();
            }}
          />
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title skeleton"
              style={{height: 60}}
            />
            <p
              className="section_heading_desc skeleton"
              style={{height: 36}}
            />
          </div>
          <div
            className="mileage_submit_box skeleton"
            style={{height: 122}}
          />
          <div
            className="mileage_submit_box skeleton"
            style={{height: 122}}
          />
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}
